import { useMemo, useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Input from "@/components/shared/ui/Input";

import AirportsTable, { type AirportTableFilters, type AirportRow } from "@/components/airports/AirportsTable";
import CreateAirport, { type UpsertAirport } from "@/components/airports/forms/CreateAirport";
import UpdateAirport from "@/components/airports/forms/UpdateAirport";

import {
  useAirportsList,
  useCreateAirportMutation,
  useUpdateAirportMutation,
  useDeleteAirportMutation,
} from "@/api/features/airports";
import type { Airport as DtoAirport } from "@/api/features/airports";

export default function ViewAirports() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<null | DtoAirport>(null);
  const [viewOpen, setViewOpen] = useState<null | DtoAirport>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("Please confirm");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [filters, setFilters] = useState<AirportTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");

  const [page, setPage] = useState(1);
  const DEFAULT_PAGE_SIZE = 20;

  useEffect(() => setPage(1), [filters, globalQ]);

  const query = useMemo(() => ({
    q: globalQ?.trim() || undefined,
    airportCode: filters.airportCode?.trim() || undefined,
    cityCode: filters.cityCode?.trim() || undefined,
    countryCode: filters.countryCode?.trim() || undefined,
  }), [globalQ, filters.airportCode, filters.cityCode, filters.countryCode]);

  const { data, isLoading, isError, error, refetch } = useAirportsList({
    page,
    limit: DEFAULT_PAGE_SIZE,
    ...query,
  });

  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_PAGE_SIZE;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pageRows: AirportRow[] = (data?.items ?? []).map((a) => ({
    id: a.id,
    airportName: a.airportName,
    airportCode: a.airportCode,
    cityName: a.cityName ?? null,
    cityCode: a.cityCode ?? null,
    countryName: a.countryName ?? null,
    countryCode: a.countryCode ?? null,
  }));

  const createMut = useCreateAirportMutation();
  const updateMut = useUpdateAirportMutation(editOpen?.id ?? 0);
  const deleteMut = useDeleteAirportMutation();

  const onCreate = async (payload: UpsertAirport) => {
    setCreateError(null);
    try {
      await createMut.mutateAsync({
        airportName: payload.airportName.trim(),
        airportCode: payload.airportCode.trim().toUpperCase(),
        cityName: payload.cityName?.trim() ? payload.cityName.trim() : null,
        cityCode: payload.cityCode?.trim() ? payload.cityCode.trim().toUpperCase() : null,
        countryName: payload.countryName?.trim() ? payload.countryName.trim() : null,
        countryCode: payload.countryCode?.trim() ? payload.countryCode.trim().toUpperCase() : null,
      });
      setCreateOpen(false);
      refetch();
    } catch (e: any) {
      setCreateError(e?.message || "Failed to create airport");
    }
  };

  const onEdit = async (payload: UpsertAirport) => {
    if (!editOpen) return;
    setUpdateError(null);
    try {
      await updateMut.mutateAsync({
        airportName: payload.airportName?.trim() ? payload.airportName.trim() : undefined,
        airportCode: payload.airportCode?.trim() ? payload.airportCode.trim().toUpperCase() : undefined,
        cityName: payload.cityName?.trim() ? payload.cityName.trim() : null,
        cityCode: payload.cityCode?.trim() ? payload.cityCode.trim().toUpperCase() : null,
        countryName: payload.countryName?.trim() ? payload.countryName.trim() : null,
        countryCode: payload.countryCode?.trim() ? payload.countryCode.trim().toUpperCase() : null,
      });
      setEditOpen(null);
      refetch();
    } catch (e: any) {
      setUpdateError(e?.message || "Failed to update airport");
    }
  };

  const askDelete = (a: AirportRow) => {
    setDeleteError(null);
    setConfirmTitle("Delete airport");
    setConfirmMsg(`Do you want to delete the airport “${a.airportCode} — ${a.airportName}”? This action cannot be undone.`);
    setConfirmCta("Delete");
    setConfirmAction(() => async () => {
      try {
        await deleteMut.mutateAsync(Number(a.id));
        setConfirmOpen(false);
        refetch();
      } catch (e: any) {
        setDeleteError(e?.message || "Failed to delete airport");
      }
    });
    setConfirmOpen(true);
  };

  const serialStart = (currentPage - 1) * pageSize;

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Airports</h1>
          <div className="mt-1"><Breadcrumbs /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <Input value={globalQ} onChange={(e) => setGlobalQ(e.target.value)} placeholder="Search airports..." className="w-56 pl-8" />
          </div>
          <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700" onClick={() => setCreateOpen(true)}><FiPlus /> Add Airport</button>
        </div>
      </div>

      {isError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">Failed to load airports: {(error as Error)?.message || "Unknown error"}</div>}
      {createError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{createError}</div>}
      {updateError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{updateError}</div>}
      {deleteError && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{deleteError}</div>}

      <AirportsTable
        pageRows={pageRows}
        serialStart={serialStart}
        filters={filters}
        setFilters={setFilters}
        onView={(r) => {
          const full = (data?.items ?? []).find((x) => x.id === r.id);
          setViewOpen(
            full ?? {
              id: Number(r.id),
              airportName: r.airportName,
              airportCode: r.airportCode,
              cityName: r.cityName ?? null,
              cityCode: r.cityCode ?? null,
              countryName: r.countryName ?? null,
              countryCode: r.countryCode ?? null,
            }
          );
        }}
        onEdit={(r) => {
          const full = (data?.items ?? []).find((x) => x.id === r.id) as DtoAirport | undefined;
          if (full) setEditOpen(full);
        }}
        onDelete={askDelete}
      />

      <div className="mt-0 flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">
          {isLoading ? <span>Loading...</span> : <>Showing <span className="font-medium text-gray-700">{total === 0 ? 0 : serialStart + 1}</span> to <span className="font-medium text-gray-700">{Math.min(serialStart + pageSize, total)}</span> of <span className="font-medium text-gray-700">{total}</span> results</>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1 || isLoading} className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Prev</button>
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === currentPage;
              return <button key={n} onClick={() => setPage(n)} className={`rounded-md px-2.5 py-1.5 text-sm ${active ? "bg-sky-600 text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>{n}</button>;
            })}
            {totalPages > 6 && <span className="px-1 text-sm text-gray-500">...</span>}
          </div>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || isLoading} className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
        </div>
      </div>

      <Modal title="Create Airport" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CreateAirport onSubmit={onCreate} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Modal title="Edit Airport" open={!!editOpen} onClose={() => setEditOpen(null)}>
        {editOpen ? (
          <UpdateAirport
            airport={{
              id: editOpen.id,
              airportName: editOpen.airportName,
              airportCode: editOpen.airportCode,
              cityName: editOpen.cityName ?? "",
              cityCode: editOpen.cityCode ?? "",
              countryName: editOpen.countryName ?? "",
              countryCode: editOpen.countryCode ?? "",
            }}
            onSubmit={onEdit}
            onCancel={() => setEditOpen(null)}
          />
        ) : null}
      </Modal>

      <Modal title="Airport Details" open={!!viewOpen} onClose={() => setViewOpen(null)}>
        {viewOpen ? (
          <dl className="divide-y divide-gray-100 text-sm">
            {[
              ["Airport", viewOpen.airportName],
              ["Airport code", viewOpen.airportCode],
              ["City", viewOpen.cityName || "—"],
              ["City code", viewOpen.cityCode || "—"],
              ["Country", viewOpen.countryName || "—"],
              ["Country code", viewOpen.countryCode || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-4 py-2.5">
                <dt className="text-gray-500">{label}</dt>
                <dd className="text-right font-medium text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Modal>

      <ConfirmDialog open={confirmOpen} title={confirmTitle} message={confirmMsg} confirmText={confirmCta} cancelText="Cancel" onCancel={() => setConfirmOpen(false)} onConfirm={confirmAction} />
    </AdminLayout>
  );
}
